import React, { useEffect, useRef, useState, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMicrophone, faStopCircle } from "@fortawesome/free-solid-svg-icons";
import SpeechRecognition, {
  useSpeechRecognition
} from "react-speech-recognition";
import "./../../../scss/component/speechToText.scss";

const SpeechToTextButton = ({
  inputRef,
  handleSpeech,        // (text) => dispatch(setChatbotState({ inputValue: text }))
  listening,            // from redux
  setListening,         // (val) => dispatch(setChatbotState({ listening: val }))
  sendDataToServer,     // sendMessage
  transcriptRef,        // useRef(""), shared with parent if needed
  isLoading,            // loading flag (optional)
  type,                 // "chatbot" (optional)
  queryType,            // unused for now
  disabled              // e.g. loading
}) => {
  const {
    transcript,
    interimTranscript,
    finalTranscript,
    resetTranscript,
    browserSupportsSpeechRecognition
  } = useSpeechRecognition();

  // Timer to detect silence
  const silenceTimerRef = useRef(null);
  // Flag to know if we should send after stop
  const pendingSendRef = useRef(false);

  const [isProcessing, setIsProcessing] = useState(false);
  const [manualEdit, setManualEdit] = useState(false);

  const clearSilenceTimer = () => {
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    silenceTimerRef.current = null;
  };

  // Optional: normalize acronyms like "d m s" => "DMS"
  const normalizeAcronyms = (text) => {
    const t = (text || "").replace(/\s+/g, " ").trim();
    return t.replace(/\b(?:[a-zA-Z]\s){1,6}[a-zA-Z]\b/g, (m) =>
      m.replace(/\s+/g, "").toUpperCase()
    );
  };

  // Auto-stop & auto-send after silence (5s)
  const resetSilenceTimer = useCallback(() => {
    clearSilenceTimer();

    silenceTimerRef.current = setTimeout(async () => {
      if (listening && !manualEdit) {
        // Only silence timeout is allowed to auto-send
        pendingSendRef.current = true;
        await stopListening();
      }
    }, 3000); // 5 seconds of silence
  }, [listening, manualEdit]);

  // Called by parent when user types manually in TextareaAutosize
  const markManualEdit = useCallback(
    (value) => {
      setManualEdit(true);
      if (transcriptRef) transcriptRef.current = value;
      if (handleSpeech) handleSpeech(value);
    },
    [handleSpeech, transcriptRef]
  );

  // Expose markManualEdit via static property (optional usage pattern)
  // Parent can ignore this and just call handleSpeech; manualEdit will still be set internally if needed.

  // Live transcript update while listening
  useEffect(() => {
    if (!listening || manualEdit) return;

    const next = normalizeAcronyms(transcript || interimTranscript || "");
    if (!next) return;

    if (transcriptRef) transcriptRef.current = next;
    if (handleSpeech) handleSpeech(next);

    if (inputRef?.current) {
      inputRef.current.value = next;
      requestAnimationFrame(() => {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(next.length, next.length);
      });
    }

    resetSilenceTimer();
  }, [
    transcript,
    interimTranscript,
    listening,
    manualEdit,
    resetSilenceTimer,
    handleSpeech,
    inputRef,
    transcriptRef
  ]);

  // Start listening
  const startListening = async () => {
    if (!browserSupportsSpeechRecognition) {
      alert("Speech recognition not supported in this browser.");
      return;
    }

    resetTranscript();
    setListening(true);
    setIsProcessing(false);
    setManualEdit(false);
    pendingSendRef.current = false;

    if (transcriptRef) transcriptRef.current = "";
    if (inputRef?.current) inputRef.current.value = "";

    resetSilenceTimer();

    await SpeechRecognition.startListening({
      continuous: true,              // keep listening until we stop[web:3]
      interimResults: true,          // get interim updates[web:31]
      language: navigator.language || "en-US",
      maxAlternatives: 3
    });
  };

  // Stop listening helper (finish processing)[web:23]
  const stopListening = async () => {
    clearSilenceTimer();
    await SpeechRecognition.stopListening();

    setListening(false);
    setIsProcessing(true);
    setTimeout(() => setIsProcessing(false), 300);
  };

  // After mic stops: if pendingSendRef is true (silence), send once
  useEffect(() => {
    if (listening) return;
    if (!pendingSendRef.current) return;

    const text = normalizeAcronyms(
      finalTranscript || transcript || interimTranscript || ""
    ).trim();

    if (sendDataToServer && text) {
      sendDataToServer(text);
    }

    pendingSendRef.current = false;
    if (transcriptRef) transcriptRef.current = "";
  }, [
    listening,
    finalTranscript,
    transcript,
    interimTranscript,
    sendDataToServer,
    transcriptRef
  ]);

  // Stop button: stop ONLY, do NOT send
  const handleStop = async () => {
    pendingSendRef.current = false; // cancel any pending auto-send
    await stopListening();
    // User can now edit TextareaAutosize and click the normal Send button
  };

  useEffect(() => {
    return () => clearSilenceTimer();
  }, []);

  if (!browserSupportsSpeechRecognition) {
    return (
      <p className="text-danger mt-2">
        ❌ Speech recognition not supported in this browser.
      </p>
    );
  }

  const isBtnDisabled = disabled || isLoading;

  return (
    <div className="button-group d-flex align-items-center gap-2">
      {!listening ? (
        <button onClick={startListening} disabled={isLoading} className="speech-to-text" aria-label="Start Voice Input">
          <FontAwesomeIcon icon={faMicrophone} />
        </button>
      ) : (
        <div className="d-flex align-items-center gap-1">
          <p className="rec-indicator">
            {isProcessing ? "Processing..." : "REC"}
          </p>
          <FontAwesomeIcon
            icon={faStopCircle}
            size="2x"
            style={{ color: "red", cursor: "pointer" }}
            onClick={handleStop}
            aria-label="Stop Voice Input"
          />
        </div>
      )}
    </div>
  );
};

export default SpeechToTextButton;
